"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import {
	ArrowRight,
	Check,
	DollarSign,
	Film,
	Loader2,
	Target,
	TrendingUp,
} from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import { highlightPricingTextFeatures } from "@/components/pricing/pricing_text_highlight";
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const quoteFormSchema = z.object({
	email: z.string().email("Please enter a valid email address"),
	name: z.string().optional(),
	channelUrl: z
		.string()
		.min(1, "Channel URL is required")
		.regex(
			/^https?:\/\/(www\.)?youtube\.com\/(@[\w-]+|channel\/[\w-]+|c\/[\w-]+)/i,
			"Please enter a valid YouTube channel URL",
		),
	videoUrl: z.string().optional(),
	targetAudience: z.string().optional(),
	message: z.string().optional(),
});

type QuoteFormInput = z.infer<typeof quoteFormSchema>;

const features = [
	{
		icon: Target,
		title: "Targeted Audience",
		description:
			"Reach viewers already interested in your niche through precise Google Ads targeting",
	},
	{
		icon: TrendingUp,
		title: "Discovery Boost",
		description:
			"Targeted campaigns put your content in front of the right audience",
	},
	{
		icon: DollarSign,
		title: "Better ROI than DIY",
		description:
			"Our optimized campaigns cost less than running ads yourself with better results",
	},
	{
		icon: Film,
		title: "Long-form Supported",
		description:
			"10+ minute videos use display ads for optimal engagement and retention",
	},
];

function QuoteDialog({
	open,
	onOpenChange,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
}) {
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isSuccess, setIsSuccess] = useState(false);

	const form = useForm<QuoteFormInput>({
		resolver: zodResolver(quoteFormSchema),
		defaultValues: {
			email: "",
			name: "",
			channelUrl: "",
			videoUrl: "",
			targetAudience: "",
			message: "",
		},
	});

	const onSubmit = async (data: QuoteFormInput) => {
		setIsSubmitting(true);

		// Format the message with video URL and target audience
		const formattedMessage = [
			"[Targeted Views Quote Request]",
			"",
			data.videoUrl
				? `Video: ${data.videoUrl}`
				: "Video: (Channel-wide campaign)",
			data.targetAudience ? `Target Audience: ${data.targetAudience}` : "",
			"",
			data.message ? `Additional Notes:\n${data.message}` : "",
		]
			.filter(Boolean)
			.join("\n");

		try {
			const response = await fetch("/api/form", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					Email: data.email,
					Name: data.name || null,
					ChannelUrl: data.channelUrl,
					Message: formattedMessage,
				}),
			});

			if (!response.ok) {
				throw new Error("Failed to submit");
			}

			setIsSuccess(true);
			toast.success("Quote request submitted! We'll be in touch soon.");
			form.reset();

			// Auto-close after success
			setTimeout(() => {
				onOpenChange(false);
				setIsSuccess(false);
			}, 2000);
		} catch (_error) {
			toast.error("Something went wrong. Please try again.");
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleOpenChange = (newOpen: boolean) => {
		if (!newOpen) {
			setIsSuccess(false);
			form.reset();
		}
		onOpenChange(newOpen);
	};

	return (
		<Dialog open={open} onOpenChange={handleOpenChange}>
			<DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
				<DialogHeader>
					<DialogTitle className="font-syne text-2xl font-bold uppercase tracking-tight">
						Get Custom Quote
					</DialogTitle>
					<DialogDescription className="font-inter text-zinc-500">
						Tell us about your channel and goals. We&apos;ll craft a
						personalized campaign plan.
					</DialogDescription>
				</DialogHeader>

				{isSuccess ? (
					<div className="py-12 text-center animate-in fade-in zoom-in-95 duration-200">
						<div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
							<Check className="w-8 h-8 text-green-600" />
						</div>
						<h3 className="font-syne font-bold text-xl mb-2">Request Sent!</h3>
						<p className="font-inter text-zinc-500">
							{highlightPricingTextFeatures(
								"We'll review your details and get back to you within 24 hours.",
							)}
						</p>
					</div>
				) : (
					<Form {...form}>
						<form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
							<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
								<FormField
									control={form.control}
									name="email"
									render={({ field }) => (
										<FormItem>
											<FormLabel className="font-inter font-medium">
												Email <span className="text-red-500">*</span>
											</FormLabel>
											<FormControl>
												<Input
													placeholder="your@email.com"
													type="email"
													{...field}
												/>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
								<FormField
									control={form.control}
									name="name"
									render={({ field }) => (
										<FormItem>
											<FormLabel className="font-inter font-medium">
												Name
											</FormLabel>
											<FormControl>
												<Input placeholder="Your name" {...field} />
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>

							<FormField
								control={form.control}
								name="channelUrl"
								render={({ field }) => (
									<FormItem>
										<FormLabel className="font-inter font-medium">
											Channel URL <span className="text-red-500">*</span>
										</FormLabel>
										<FormControl>
											<Input
												placeholder="https://youtube.com/@yourchannel"
												{...field}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>

							<FormField
								control={form.control}
								name="videoUrl"
								render={({ field }) => (
									<FormItem>
										<FormLabel className="font-inter font-medium">
											Video URL{" "}
											<span className="text-zinc-400 font-normal">
												(optional)
											</span>
										</FormLabel>
										<FormControl>
											<Input
												placeholder="https://youtube.com/watch?v=... (leave blank for channel-wide)"
												{...field}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>

							<FormField
								control={form.control}
								name="targetAudience"
								render={({ field }) => (
									<FormItem>
										<FormLabel className="font-inter font-medium">
											Target Audience{" "}
											<span className="text-zinc-400 font-normal">
												(optional)
											</span>
										</FormLabel>
										<FormControl>
											<Input
												placeholder="e.g., Tech enthusiasts, Gamers 18-35, Cooking hobbyists in USA..."
												{...field}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>

							<FormField
								control={form.control}
								name="message"
								render={({ field }) => (
									<FormItem>
										<FormLabel className="font-inter font-medium">
											Additional Details{" "}
											<span className="text-zinc-400 font-normal">
												(optional)
											</span>
										</FormLabel>
										<FormControl>
											<Textarea
												placeholder="Tell us about your goals: budget range, timeline, specific requirements..."
												className="min-h-[100px] resize-none"
												{...field}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>

							<Button
								type="submit"
								disabled={isSubmitting}
								className="w-full h-12 bg-red-600 hover:bg-red-700 text-white font-syne font-bold uppercase tracking-wide rounded-xl"
							>
								{isSubmitting ? (
									<>
										<Loader2 className="w-4 h-4 mr-2 animate-spin" />
										Submitting...
									</>
								) : (
									<>
										Submit Request
										<ArrowRight className="w-4 h-4 ml-2" />
									</>
								)}
							</Button>
						</form>
					</Form>
				)}
			</DialogContent>
		</Dialog>
	);
}

export default function TargetedPricingTab() {
	const [isQuoteOpen, setIsQuoteOpen] = useState(false);

	return (
		<>
			<QuoteDialog open={isQuoteOpen} onOpenChange={setIsQuoteOpen} />

			<section className="w-full mb-12">
				{/* Hero Section */}
				<div className="text-center mb-12 max-w-3xl mx-auto">
					<div className="animate-on-scroll-view">
						<div className="inline-flex items-center gap-2 bg-red-50 text-red-600 px-4 py-2 rounded-full mb-6">
							<Target className="w-4 h-4" />
							<span className="font-inter font-semibold text-sm uppercase tracking-wide">
								Premium Service
							</span>
						</div>
						<h2 className="font-syne font-black text-3xl md:text-4xl uppercase tracking-tight text-zinc-900 mb-4">
							Targeted Views
						</h2>
						<p className="font-inter text-zinc-500 text-base md:text-lg leading-relaxed">
							Premium video advertising through Google Ads targeting your niche
							audience.
						</p>
					</div>
				</div>

				{/* Features Grid */}
				<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-5xl mx-auto mb-12">
					{features.map((feature) => (
						<div
							key={feature.title}
							className="bg-white border border-zinc-100 rounded-2xl p-6 hover:border-zinc-200 hover:shadow-lg transition-[border-color,box-shadow] animate-on-scroll-view"
						>
							<div className="w-10 h-10 bg-zinc-50 rounded-xl flex items-center justify-center mb-4">
								<feature.icon className="w-5 h-5 text-zinc-700" />
							</div>
							<h3 className="font-syne font-bold text-sm uppercase tracking-wide text-zinc-900 mb-2">
								{feature.title}
							</h3>
							<p className="font-inter text-sm text-zinc-500 leading-relaxed">
								{highlightPricingTextFeatures(feature.description)}
							</p>
						</div>
					))}
				</div>

				{/* CTA Section */}
				<div className="bg-linear-to-br from-zinc-900 to-zinc-800 rounded-3xl p-8 md:p-12 max-w-3xl mx-auto text-center animate-on-scroll-view">
					<h3 className="font-syne font-bold text-2xl md:text-3xl uppercase tracking-tight text-white mb-4">
						Custom Pricing for Your Goals
					</h3>
					<p className="font-inter text-zinc-400 mb-2">
						Pricing varies based on video length, target audience, and campaign
						scope.
					</p>
					<p className="font-inter text-zinc-300 mb-8">
						<span className="text-white font-semibold">
							{highlightPricingTextFeatures("Starting at ~$70-80")}
						</span>{" "}
						{highlightPricingTextFeatures("for 10,000 targeted views")}
					</p>

					<Button
						type="button"
						onClick={() => setIsQuoteOpen(true)}
						className="h-14 px-6! bg-red-600 hover:bg-red-700 text-white font-syne font-bold uppercase tracking-wide rounded-full shadow-lg hover:shadow-xl transition-[background-color,box-shadow,transform] hover:-translate-y-1"
					>
						Get Custom Quote
						<ArrowRight className="w-5 h-5 ml-1" />
					</Button>
				</div>
			</section>

			{/* FAQ Section */}
			<div className="w-full max-w-3xl mx-auto mb-20">
				<div className="text-center mb-12">
					<h2 className="font-syne font-black text-3xl uppercase tracking-tight text-zinc-900">
						Targeted Views FAQ
					</h2>
					<div className="h-1 w-12 bg-zinc-200 mx-auto mt-4 rounded-full" />
				</div>

				<Accordion type="single" collapsible className="w-full space-y-4">
					<AccordionItem
						value="item-1"
						className="border border-zinc-200 rounded-2xl px-4 overflow-hidden bg-white/50 hover:bg-white transition-colors"
					>
						<AccordionTrigger className="font-syne font-bold text-lg text-zinc-700 hover:text-zinc-900 hover:no-underline py-6">
							Why custom pricing instead of fixed plans?
						</AccordionTrigger>
						<AccordionContent className="font-inter text-zinc-500 leading-relaxed pb-6">
							{highlightPricingTextFeatures(
								"Video length significantly affects ad costs. Videos under 3 minutes can use skippable ads, while longer videos (10+ minutes) require display ads which cost more on Google&apos;s side. Custom pricing ensures you get the best value for your specific content.",
							)}
						</AccordionContent>
					</AccordionItem>

					<AccordionItem
						value="item-2"
						className="border border-zinc-200 rounded-2xl px-4 overflow-hidden bg-white/50 hover:bg-white transition-colors"
					>
						<AccordionTrigger className="font-syne font-bold text-lg text-zinc-700 hover:text-zinc-900 hover:no-underline py-6">
							What&apos;s the difference between targeted and generic views?
						</AccordionTrigger>
						<AccordionContent className="font-inter text-zinc-500 leading-relaxed pb-6">
							{highlightPricingTextFeatures(
								"Targeted campaigns reach users already interested in your niche through precise Google Ads targeting. This means more engaged viewers who stick around. Standard campaigns are more affordable but less precise for creators with quality content that would benefit from a highly engaged audience.",
							)}
						</AccordionContent>
					</AccordionItem>

					<AccordionItem
						value="item-3"
						className="border border-zinc-200 rounded-2xl px-4 overflow-hidden bg-white/50 hover:bg-white transition-colors"
					>
						<AccordionTrigger className="font-syne font-bold text-lg text-zinc-700 hover:text-zinc-900 hover:no-underline py-6">
							How does this help my channel grow long-term?
						</AccordionTrigger>
						<AccordionContent className="font-inter text-zinc-500 leading-relaxed pb-6">
							{highlightPricingTextFeatures(
								"When the right viewers discover your content through our campaigns, it creates a ripple effect. Engaged viewers share, comment, and return — building momentum that extends well beyond the campaign itself.",
							)}
						</AccordionContent>
					</AccordionItem>

					<AccordionItem
						value="item-4"
						className="border border-zinc-200 rounded-2xl px-4 overflow-hidden bg-white/50 hover:bg-white transition-colors"
					>
						<AccordionTrigger className="font-syne font-bold text-lg text-zinc-700 hover:text-zinc-900 hover:no-underline py-6">
							How long does delivery take?
						</AccordionTrigger>
						<AccordionContent className="font-inter text-zinc-500 leading-relaxed pb-6">
							{highlightPricingTextFeatures(
								"Delivery time depends on campaign size and targeting specificity. Typically, campaigns reach ~10,000 viewers within 7-14 days. We pace campaigns naturally to maintain high-quality engagement patterns.",
							)}
						</AccordionContent>
					</AccordionItem>

					<AccordionItem
						value="item-5"
						className="border border-zinc-200 rounded-2xl px-4 overflow-hidden bg-white/50 hover:bg-white transition-colors"
					>
						<AccordionTrigger className="font-syne font-bold text-lg text-zinc-700 hover:text-zinc-900 hover:no-underline py-6">
							Is this safe for my channel?
						</AccordionTrigger>
						<AccordionContent className="font-inter text-zinc-500 leading-relaxed pb-6">
							Yes, completely. We use legitimate Google Ads campaigns to promote
							your content. This is the same advertising method used by major
							brands and creators. Views come from real users who see your video
							through Google&apos;s ad network.
						</AccordionContent>
					</AccordionItem>
				</Accordion>
			</div>
		</>
	);
}
